import { useState } from 'react';
import { Link } from 'react-router-dom';
import { wordpressApi } from '../../../api';
import { ErrorNotice, LoadingRows } from '../../../components/ui';
import { DeploymentHistory } from '../../../components/deploy/DeploymentHistory';
import { StatusBand } from '../../../components/project/StatusBand';
import {
  FactList,
  PanelCard,
  ProjectCol,
  ProjectGrid,
  ProjectSection,
  QuietEmpty,
  SectionHeader,
} from '../../../components/project/ui';
import { Callout, SummaryTile, SummaryTiles } from '../../../components/primitives';
import type { AttentionItem, ProjectHealth, SetupProgress } from '../../../components/project/projectHealth';
import { relativeTime } from '../../../components/project/projectHealth';
import { securityCoverage, securityLevel, siteLevel, softwareCoverage, updatesLevel } from '../../../components/project/wordpressHealth';
import { AttentionPanel } from '../OverviewSection';
import { useProject } from '../ProjectContext';
import { MonitorHealthList } from './MonitorHealthList';

/**
 * WordPressOverview answers the same questions as the Magento Overview
 * (CO-150) — is the live site working, what happened to the latest
 * deployment, does the infrastructure warn, what needs me — then the
 * WordPress and WooCommerce facts that inspection, the software check and
 * the monitors recorded.
 */
export function WordPressOverview({
  attention,
  setup,
}: {
  /** The attention level; the Overview reads the dimensions instead. */
  health?: ProjectHealth;
  attention: AttentionItem[];
  setup: SetupProgress;
}) {
  const project = useProject();
  const { environment, sectionPath, deployments, wordpressSite } = project;
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<Error | null>(null);

  if (!environment) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-environment">
          No environments
          <Link className="button button--primary button--small quiet-empty__action" to={sectionPath('settings')}>
            Add an environment
          </Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  const site = wordpressSite.data;
  if (!site && !wordpressSite.loading) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-wordpress-site">
          {environment.name} · No WordPress site
          <Link className="button button--primary button--small quiet-empty__action" to={sectionPath('site')}>
            Connect the site
          </Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  const deploymentList = deployments.data?.deployments ?? [];
  const inspection = site?.inspection;
  const woo = inspection?.site.woocommerce;
  const software = site?.software?.summary;
  const security = site?.security;
  const softwareGap = softwareCoverage(site ?? null);
  const securityGap = securityCoverage(site ?? null);

  async function retryInspection() {
    if (!site) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await wordpressApi.inspect(site.id);
      project.refresh();
    } catch (err) {
      setRetryError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setRetrying(false);
    }
  }

  return (
    <ProjectSection>
      {setup.next ? (
        <div className="setup-banner" data-testid="setup-banner">
          <div>
            <strong aria-label={`${setup.done} of ${setup.total} setup steps done`}>
              Setup {setup.done}/{setup.total}
            </strong>
            <span className="setup-banner__next">Next: {setup.next}</span>
          </div>
          <Link
            className="button button--secondary"
            to={sectionPath(setup.nextKey === 'repository' || setup.nextKey === 'access' ? 'repositories' : 'site')}
          >
            Continue
          </Link>
        </div>
      ) : null}

      <StatusBand
        health={project.environmentHealth.data}
        loading={project.environmentHealth.loading && !project.environmentHealth.data}
        error={project.environmentHealth.error}
        sectionPath={sectionPath}
      />

      {site?.inspection_error ? (
        <Callout
          tone="warning"
          title="The last inspection failed"
          testId="wp-inspection-failed"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={retryInspection} disabled={retrying}>
              {retrying ? 'Inspecting' : 'Retry'}
            </button>
          }
        >
          <p>{site.inspection_error}</p>
          {inspection ? (
            <FactList facts={[{ label: 'Last good inspection', value: relativeTime(site.inspected_at) }]} />
          ) : null}
          <ErrorNotice error={retryError} />
        </Callout>
      ) : null}

      <SummaryTiles label="WordPress">
        <SummaryTile
          label="WordPress"
          level={siteLevel(site)}
          headline={
            inspection
              ? inspection.site.bootstrapped
                ? `WordPress ${inspection.site.wp_version ?? ''}`.trim()
                : 'Does not load with its plugins'
              : wordpressSite.loading
                ? 'Loading…'
                : 'Not inspected yet'
          }
          detail={
            inspection
              ? `${woo ? `WooCommerce ${woo.version}` : 'WooCommerce not active'} · PHP ${inspection.site.php_version ?? 'not known'} · inspected ${relativeTime(site?.inspected_at)}`
              : site?.inspection_error || undefined
          }
          link={{ label: 'View site', to: sectionPath('site') }}
          testId="summary-site"
        />
        <SummaryTile
          label="Updates"
          level={updatesLevel(site)}
          headline={
            software
              ? software.updates_available > 0
                ? `${software.updates_available} ${software.updates_available === 1 ? 'update' : 'updates'} available`
                : !softwareGap.complete
                  ? 'Not fully checked'
                  : software.vulnerable > 0
                    ? 'No updates available'
                    : 'Up to date'
              : 'Not checked yet'
          }
          detail={
            software
              ? [
                  software.vulnerable > 0 ? `${software.vulnerable} with a known vulnerability` : null,
                  software.security_updates > 0 ? `${software.security_updates} security` : null,
                  software.major_updates > 0 ? `${software.major_updates} major` : null,
                  softwareGap.complete ? null : software.updates_available === 0 ? `No updates found · ${softwareGap.gap}` : softwareGap.gap,
                  `checked ${relativeTime(site?.software_checked_at)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          link={{ label: 'View updates', to: sectionPath('updates') }}
          testId="summary-updates"
        />
        <SummaryTile
          label="Security"
          level={securityLevel(site)}
          headline={
            security
              ? (security.findings ?? []).length === 0 && !securityGap.complete
                ? 'Not fully checked'
                : `${(security.findings ?? []).length} ${(security.findings ?? []).length === 1 ? 'finding' : 'findings'}`
              : 'Not checked yet'
          }
          detail={
            security
              ? `Hardening score ${security.security_score}/100 · checked ${relativeTime(security.checked_at)}${securityGap.complete ? '' : ` · ${securityGap.gap}`}`
              : undefined
          }
          link={{ label: 'View security', to: sectionPath('updates') }}
          testId="summary-security"
        />
      </SummaryTiles>

      <AttentionPanel
        items={attention}
        loading={(wordpressSite.loading && attention.length === 0 && !site) || (project.environmentHealth.loading && !project.environmentHealth.data)}
        error={project.environmentHealth.error}
        onRetry={project.environmentHealth.reload}
        dismissal={{ environmentId: environment.id, dismissed: project.environmentHealth.data?.dismissed ?? [], onChanged: project.environmentHealth.reload }}
      />

      <ProjectGrid>
        <ProjectCol span={8}>
          <PanelCard labelledBy="wp-overview-deployments" testId="recent-deployments">
            <SectionHeader
              id="wp-overview-deployments"
              title="Recent deployments"
              actions={
                <Link className="button button--secondary button--small" to={sectionPath('deployments')}>
                  All deployments
                </Link>
              }
            />
            {deployments.loading && deploymentList.length === 0 ? (
              <LoadingRows label="Loading deployments" rows={3} rowHeight="40px" />
            ) : deploymentList.length === 0 ? (
              <QuietEmpty testId="no-deployments">
                {project.project.repository_url ? 'No deployments' : 'No deployments · No repository'}
              </QuietEmpty>
            ) : (
              <DeploymentHistory
                deployments={deploymentList.slice(0, 5)}
                serving={deployments.data?.serving}
                variant="compact"
                deploymentPath={(id) => sectionPath('deployments', { deployment: id })}
              />
            )}
          </PanelCard>

          <PanelCard labelledBy="wp-overview-checks" testId="wp-checks">
            <SectionHeader
              id="wp-overview-checks"
              title="Shop checks"
              actions={
                <Link className="button button--secondary" to={sectionPath('monitoring')}>
                  Open monitoring
                </Link>
              }
            />
            <MonitorHealthList />
          </PanelCard>
        </ProjectCol>

        <ProjectCol span={4}>
          <PanelCard labelledBy="wp-overview-store" testId="wp-store-facts">
            <SectionHeader id="wp-overview-store" title={woo ? 'WooCommerce' : 'WordPress'} />
            {inspection ? (
              <FactList
                facts={[
                  { label: 'Site', value: inspection.site.home ? <code>{inspection.site.home}</code> : <span className="muted">not known</span> },
                  ...(woo
                    ? [
                        { label: 'Currency', value: woo.currency || <span className="muted">not set</span> },
                        {
                          label: 'Order storage',
                          value:
                            woo.hpos === true
                              ? 'High-performance order tables'
                              : woo.hpos === false
                                ? 'WordPress posts table'
                                : <span className="muted">not known</span>,
                        },
                        { label: 'Published products', value: `${site?.health_baseline.products_published ?? 0}` },
                        { label: 'Payment methods on', value: `${site?.health_baseline.gateways_enabled ?? 0}` },
                      ]
                    : []),
                  { label: 'Active plugins', value: `${inspection.site.active_plugins?.length ?? 0}` },
                  { label: 'Theme', value: inspection.site.active_theme || <span className="muted">not known</span> },
                ]}
              />
            ) : (
              <QuietEmpty>Not inspected yet</QuietEmpty>
            )}
            <Link className="panel__link" to={sectionPath('site')}>
              View site
            </Link>
          </PanelCard>

          <PanelCard labelledBy="wp-overview-distribution" testId="wp-distribution-summary">
            <SectionHeader id="wp-overview-distribution" title="Product distribution" />
            <Link className="panel__link" to={sectionPath('distribution')}>
              Open product distribution
            </Link>
          </PanelCard>

          <PanelCard labelledBy="wp-overview-environment" testId="environment-facts">
            <SectionHeader id="wp-overview-environment" title="This environment" />
            <FactList
              facts={[
                { label: 'Name', value: environment.name },
                {
                  label: 'Server',
                  value: site ? (
                    <Link to={`/app/servers/${site.server_id}`}>{project.serverName(site.server_id)}</Link>
                  ) : (
                    <span className="muted">not connected</span>
                  ),
                },
                { label: 'WordPress directory', value: site ? <code>{site.root_path}</code> : <span className="muted">—</span> },
              ]}
            />
            <Link className="panel__link" to={`/app/environments/${environment.id}`}>
              Environment settings
            </Link>
          </PanelCard>
        </ProjectCol>
      </ProjectGrid>
    </ProjectSection>
  );
}
